(() => {
  const params = new URLSearchParams(window.location.search);
  const productReference =
    params.get('ad_product') || params.get('ad_product_url');

  if (!productReference) return;

  const extractHandle = (value) => {
    if (!value) return null;

    try {
      const url = new URL(value, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/i);

      if (match) {
        return decodeURIComponent(match[1]).toLowerCase();
      }
    } catch (error) {
      // Treat the value as a possible plain product handle.
    }

    const possibleHandle = String(value).trim().toLowerCase();

    return /^[a-z0-9][a-z0-9-]*$/.test(possibleHandle)
      ? possibleHandle
      : null;
  };

  const targetHandle = extractHandle(productReference);

  if (!targetHandle) return;

  const gridSelector = [
    '#ResultsList [data-testid="product-grid"]',
    '#ResultsList ul.product-grid',
    '#product-grid',
  ].join(', ');

  const getGrid = (root = document) => root.querySelector(gridSelector);

  const getSectionId = (grid) => {
    const resultsList = grid.closest('results-list[section-id]');

    if (resultsList) {
      return resultsList.getAttribute('section-id');
    }

    const sectionWrapper = grid.closest('[id^="shopify-section-"]');

    return sectionWrapper
      ? sectionWrapper.id.replace(/^shopify-section-/, '')
      : null;
  };

  const getCardHandle = (card) => {
    const declaredHandle = card.dataset.productHandle;

    if (declaredHandle) {
      return declaredHandle.toLowerCase();
    }

    for (const link of card.querySelectorAll('a[href*="/products/"]')) {
      const handle = extractHandle(link.getAttribute('href'));

      if (handle) return handle;
    }

    return null;
  };

  const findCard = (grid) => {
    if (!grid) return null;

    return Array.from(grid.children).find(
      (card) => getCardHandle(card) === targetHandle
    );
  };

  const getLastPage = (root, grid) => {
    let lastPage = Number.parseInt(grid?.dataset.lastPage || '1', 10);

    if (!Number.isFinite(lastPage) || lastPage < 1) {
      lastPage = 1;
    }

    for (const link of root.querySelectorAll('a[href*="page="]')) {
      try {
        const page = Number.parseInt(
          new URL(link.href, window.location.origin).searchParams.get('page') ||
            '1',
          10
        );

        if (Number.isFinite(page)) {
          lastPage = Math.max(lastPage, page);
        }
      } catch (error) {
        // Ignore malformed pagination links.
      }
    }

    return Math.min(lastPage, 20);
  };

  const fetchCardFromAnotherPage = async (grid) => {
    const lastPage = getLastPage(document, grid);

    if (lastPage < 2) return null;

    const currentPage =
      Number.parseInt(params.get('page') || '1', 10) || 1;

    const sectionId = getSectionId(grid);
    const pages = [];

    for (let page = 1; page <= lastPage; page += 1) {
      if (page !== currentPage) {
        pages.push(page);
      }
    }

    const loadPage = async (page, signal) => {
      const pageUrl = new URL(window.location.href);

      pageUrl.searchParams.delete('ad_product');
      pageUrl.searchParams.delete('ad_product_url');
      pageUrl.searchParams.set('page', String(page));

      if (sectionId) {
        pageUrl.searchParams.set('section_id', sectionId);
      }

      try {
        let response = await fetch(pageUrl.toString(), {
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html',
          },
          signal,
        });

        if (!response.ok && sectionId) {
          pageUrl.searchParams.delete('section_id');

          response = await fetch(pageUrl.toString(), {
            credentials: 'same-origin',
            headers: {
              Accept: 'text/html',
            },
            signal,
          });
        }

        if (!response.ok) return null;

        const fetchedDocument = new DOMParser().parseFromString(
          await response.text(),
          'text/html'
        );

        const fetchedCard = findCard(getGrid(fetchedDocument));

        return fetchedCard
          ? document.importNode(fetchedCard, true)
          : null;
      } catch (error) {
        return null;
      }
    };

    return new Promise((resolve) => {
      let unresolved = pages.length;
      let completed = false;

      const controllers = pages.map(() => new AbortController());

      if (unresolved === 0) {
        resolve(null);
        return;
      }

      pages.forEach((page, index) => {
        loadPage(page, controllers[index].signal).then((card) => {
          if (completed) return;

          if (card) {
            completed = true;

            controllers.forEach((controller) => {
              controller.abort();
            });

            resolve(card);
            return;
          }

          unresolved -= 1;

          if (unresolved === 0) {
            resolve(null);
          }
        });
      });
    });
  };

  const start = async () => {
    const grid = getGrid();

    if (!grid) return;

    let pinnedCard = findCard(grid);

    if (!pinnedCard) {
      pinnedCard = await fetchCardFromAnotherPage(grid);
    }

    if (!pinnedCard) return;

    for (const image of pinnedCard.querySelectorAll('img')) {
      image.loading = 'eager';
      image.fetchPriority = 'high';
    }

    pinnedCard.dataset.adProductPinned = 'true';
    grid.prepend(pinnedCard);

    const observerRoot =
      document.querySelector('results-list') || grid.parentElement;

    if (!observerRoot) return;

    const observer = new MutationObserver(() => {
      const liveGrid = getGrid();

      if (!liveGrid) return;

      if (pinnedCard.parentElement !== liveGrid) {
        const replacement = findCard(liveGrid);

        if (!replacement) return;

        pinnedCard = replacement;
        pinnedCard.dataset.adProductPinned = 'true';
      }

      for (const card of Array.from(liveGrid.children)) {
        if (
          card !== pinnedCard &&
          getCardHandle(card) === targetHandle
        ) {
          card.remove();
        }
      }

      if (liveGrid.firstElementChild !== pinnedCard) {
        liveGrid.prepend(pinnedCard);
      }
    });

    observer.observe(observerRoot, {
      childList: true,
      subtree: true,
    });
  };

  if (getGrid()) {
    start();
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, {
      once: true,
    });
  } else {
    start();
  }
})();